import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { ApiError, auth, currentOrgId, setAccessToken, setAccessVersion, setOrgId } from "../api/client";
import type { Staff, StaffLoginOut } from "../api/types";
import { clearOfflineCache, keepLookups, offlineProfile, startOfflineCache, type OfflineProfile } from "../pwa/offlineCache";
import { clearOutbox, pendingCount, setOutboxOwner } from "../pwa/outbox";
import { releaseBlocks } from "../pwa/numberBlocks";
import { check, isOnline, onNetwork } from "../pwa/network";
import { deviceOrg, restoreDeviceLocale, startOrgCheck, stopOrgCheck } from "../pwa/orgCheck";
import { t } from "../i18n";

/*
 * Staff sign-in, the same two steps as the admin panel: password, then the
 * six-digit code from the authenticator app (Backend-v2 /admin/auth/*).
 * The token lives in memory only (see client.ts); a reload gets the session
 * back from the server's httpOnly cookie before showing the sign-in page.
 * The phone app (src/pwa): a reload with no connection, by someone whose offline copies have not expired, opens the
 * read-only offline screen ("offline") instead, and the session is asked for again as soon as the server answers.
 * Sign-out deletes the offline copies and anything still waiting in the outbox; an expired session deletes the copies.
 * A role change applies without a reload: when a staff answer's X-Access-Version differs (client.ts), and every minute
 * while the tab is visible, the session is asked for again and the role and access are replaced, so the shell and the
 * screens re-draw what this person may open and do.
 */

type Step = { kind: "restoring" } | { kind: "signed_out" } | { kind: "mfa"; pending: string; email: string } | { kind: "signed_in"; staff: Staff } | { kind: "offline"; profile: OfflineProfile };

type AuthValue = {
  step: Step;
  staff: Staff | null;
  can: (permission: string) => boolean;
  /** What this role may do in a module (leads, billing, settings...). The server enforces it again on every call. */
  allow: (module: string, action?: "view" | "create" | "edit" | "delete" | "approve") => boolean;
  signIn: (email: string, password: string) => Promise<void>;
  verify: (code: string) => Promise<void>;
  restart: () => void;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [step, setStep] = useState<Step>({ kind: "restoring" });

  const accept = useCallback((res: StaffLoginOut, email: string) => {
    if (res.status === "ok" && res.access_token && res.staff) {
      setAccessToken(res.access_token);
      setAccessVersion(res.access_version ?? null);
      // Every call from now on goes to this organization (X-Org-Id); the role and access above are this organization's.
      const org = res.organization?.id ?? 1;
      setOrgId(res.organization?.id ?? null);
      const staff: Staff = { ...res.staff, permissions: res.permissions ?? [], role: res.role ?? null, access: res.access ?? {}, organization: res.organization ?? null, organizations: res.organizations ?? [] };
      // Offline copies are kept per organization; the outbox belongs to the person and sends each item to its own organization.
      const member = staff.organizations.length ? staff.organizations.map((o) => o.id) : [org];
      void startOfflineCache({ id: staff.id, full_name: staff.full_name, email: staff.email, role: staff.role?.name ?? null, org, org_name: res.organization?.name ?? null, access: staff.access }, member).then(() => setOutboxOwner(staff.id, res.organization?.name ?? null)).then(keepLookups); // wave 9: what the full forms need offline
      // Wave 6: this device's organization, and copies and outbox items of an organization left, checked while online.
      startOrgCheck(member);
      setStep({ kind: "signed_in", staff });
    } else if (res.status === "mfa_required" && res.pending_token) {
      setStep({ kind: "mfa", pending: res.pending_token, email });
    } else if (res.status === "mfa_enrollment_required") {
      // Enrolment shows a secret once; that flow stays in the admin panel.
      throw new Error("Set up two-factor sign-in in the admin panel first, then sign in here.");
    } else {
      throw new Error("Sign-in did not complete. Try again.");
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string) => accept(await auth.login(email, password), email), [accept]);

  const verify = useCallback(
    async (code: string) => {
      if (step.kind !== "mfa") return;
      accept(await auth.verifyMfa(step.pending, code), step.email);
    },
    [step, accept],
  );

  const signOut = useCallback(async () => {
    const waiting = await pendingCount();
    if (waiting && !window.confirm(t("{waiting} {v} made offline {v2} not been sent yet. Signing out deletes {v3} from this device. Sign out anyway?", { waiting, v: waiting === 1 ? t("change") : t("changes"), v2: waiting === 1 ? t("has") : t("have"), v3: waiting === 1 ? "it" : t("them") }))) return;
    stopOrgCheck();
    await releaseBlocks().catch(() => undefined); // wave 7: this device's unused invoice numbers back to the server's pool, while it can
    await Promise.all([clearOfflineCache(), clearOutbox()]);
    try {
      await auth.logout();
    } catch {
      // Signing out locally still matters if the server call fails.
    }
    setAccessToken(null);
    setAccessVersion(null);
    setOrgId(null);
    setStep({ kind: "signed_out" });
  }, []);

  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    // A window with no organization of its own (a shortcut, a new tab) asks for the one this device showed last.
    (currentOrgId() ? Promise.resolve(null) : deviceOrg())
      .then((d) => {
        if (d && !currentOrgId()) setOrgId(d);
        return auth.session();
      })
      .then((res) => {
        if (cancelled) return;
        if (res?.status === "ok" && res.access_token && res.staff) accept(res, res.staff.email);
        else {
          void clearOfflineCache();
          setStep({ kind: "signed_out" });
        }
      })
      .catch(async (e: unknown) => {
        // No connection: the offline screen, if this person's copies are still fresh. Otherwise the sign-in page shows the way forward.
        const profile = e instanceof ApiError && e.status === 0 ? await offlineProfile() : null;
        if (profile) {
          // The offline screen works in the profile's organization, and what it records waits in the outbox for it.
          if (!currentOrgId()) setOrgId(profile.org);
          await setOutboxOwner(profile.id, profile.org_name);
          await restoreDeviceLocale();
        }
        if (!cancelled) setStep(profile ? { kind: "offline", profile } : { kind: "signed_out" });
      });
    return () => {
      cancelled = true;
    };
  }, [accept, attempt]);

  // Offline screen: ask for the session again once the server answers.
  const offline = step.kind === "offline";
  useEffect(() => {
    if (!offline) return;
    void check();
    return onNetwork(() => isOnline() && setAttempt((n) => n + 1));
  }, [offline]);

  useEffect(() => {
    const expired = () => {
      stopOrgCheck();
      setAccessToken(null);
      void clearOfflineCache();
      void setOutboxOwner(null);
      setStep({ kind: "signed_out" });
    };
    window.addEventListener("crm:session-expired", expired);
    return () => window.removeEventListener("crm:session-expired", expired);
  }, []);

  // Live role changes: replace the role and access in place (nothing else of the session is redone).
  const signedIn = step.kind === "signed_in";
  useEffect(() => {
    if (!signedIn) return;
    let busy = false;
    const refresh = async () => {
      if (busy || document.visibilityState === "hidden") return;
      busy = true;
      try {
        const res = await auth.session();
        if (res?.status !== "ok" || !res.staff) return;
        setAccessVersion(res.access_version ?? null);
        // The organizations too: one made or archived here (crm:orgs-changed) shows in the switcher without a reload.
        setStep((s) => (s.kind === "signed_in" && s.staff.id === res.staff?.id ? { kind: "signed_in", staff: { ...s.staff, role: res.role ?? null, access: res.access ?? {}, permissions: res.permissions ?? s.staff.permissions, organization: res.organization ?? s.staff.organization, organizations: res.organizations ?? s.staff.organizations } } : s));
      } catch {
        // Offline or the server restarting: the next answer or the next minute tries again.
      } finally {
        busy = false;
      }
    };
    const timer = window.setInterval(refresh, 60_000);
    window.addEventListener("crm:access-changed", refresh);
    window.addEventListener("crm:orgs-changed", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("crm:access-changed", refresh);
      window.removeEventListener("crm:orgs-changed", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [signedIn]);

  const value = useMemo<AuthValue>(() => {
    const staff = step.kind === "signed_in" ? step.staff : null;
    return {
      step,
      staff,
      can: (p) => Boolean(staff?.permissions.includes(p)),
      allow: (module, action = "view") => Boolean(staff?.access[module]?.includes(action)),
      signIn,
      verify,
      restart: () => setStep({ kind: "signed_out" }),
      signOut,
    };
  }, [step, signIn, verify, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
